import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Mail,
  Phone,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ShieldAlert,
  Hash,
} from "lucide-react";
import KioskLayout from "@/kiosk/KioskLayout";
import SignaturePad from "@/kiosk/SignaturePad";
import kioskApi, { getKioskToken } from "@/lib/kioskApi";
import { sanitizeHtml } from "@/lib/safeHtml";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * Phase C1.5 — Module 7 Returning Visitor fast lane.
 *
 * Flow:
 *   1. Visitor enters email OR phone.
 *   2. We call `/kiosk/visitor-lookup` (kiosk-token-authed, org-scoped).
 *   3. Backend returns `{found, visitor, consent_status, expired_consent_doc_ids}`.
 *   4. Branches:
 *        - found + consent valid  → confirm screen, then one-tap sign-in.
 *        - found + consent expired → re-accept the moved-on consents in-page,
 *          then one-tap sign-in.
 *        - not found              → "We don't have you on file — sign in
 *          the usual way", routes to /kiosk/sign-in.
 *
 * UX rules:
 *   • Returning lane skips the long form and the photo step (photo_bypass
 *     is audited server-side as `kiosk.photo_bypass_used`). The signature
 *     canvas IS mounted when the matched visitor's type requires one —
 *     legal sign-off cannot be bypassed.
 *   • If consents are expired we still require a re-acceptance — re-consent
 *     is a legal need, not just a form-completion preference.
 */
function readKioskToken() {
  const tok = getKioskToken();
  if (!tok) return null;
  try { return JSON.parse(atob(tok.split(".")[1])); }
  catch { return null; }
}

const PHASES = {
  lookup: "lookup",
  consents: "consents",
  confirm: "confirm",
  done: "done",
};

export default function KioskWelcomeBack() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tokenInfo = readKioskToken();
  const [branding, setBranding] = useState(null);
  const [identifier, setIdentifier] = useState("");
  const [mode, setMode] = useState("email");   // "email" | "phone"
  const [phase, setPhase] = useState(PHASES.lookup);
  const [match, setMatch] = useState(null);
  const [visitorTypes, setVisitorTypes] = useState([]);
  // Phase C1.5 — we track BOTH the full set of currently-active consents
  // (`allActiveConsents`) AND the subset that needs explicit re-acceptance
  // (`pendingConsents`). The submission sends every active consent doc — for
  // the ones already accepted on the prior visit, the simple act of pressing
  // "Sign me in" reaffirms them; the expired ones additionally require an
  // explicit checkbox tick on the consents page.
  const [allActiveConsents, setAllActiveConsents] = useState([]);
  const [pendingConsents, setPendingConsents] = useState([]);
  const [accepted, setAccepted] = useState({});
  const [signature, setSignature] = useState(null);
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!tokenInfo) { nav("/kiosk/pair", { replace: true }); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sites/${tokenInfo.site_id}/public-branding`);
        setBranding(data);
      } catch { /* non-blocking */ }
      // Phase C1.5 fix — we need each visitor type's required_fields so the
      // confirm screen mounts the signature canvas (or photo capture, in
      // future) when the matched visitor's stored type demands it.
      try {
        const tRes = await kioskApi.get("/visitor-types/active");
        setVisitorTypes(tRes.data || []);
      } catch { /* non-blocking */ }
    })();
  }, [nav, tokenInfo?.site_id]);

  // Derive required_fields for the matched visitor's type. When the type
  // isn't found (e.g. admin renamed it), default to "nothing required" so
  // the kiosk doesn't block a legitimate returning visitor — the backend
  // will surface a clean 422 if it still requires a signature.
  const matchedType = useMemo(
    () => visitorTypes.find((vt) => vt.name === (match?.visitor_type || "Visitor")) || null,
    [visitorTypes, match?.visitor_type],
  );
  const requiredFields = matchedType?.required_fields || [];
  const requiresSignature = requiredFields.includes("signature");
  const requiresPhoto = requiredFields.includes("photo");

  const isValidIdentifier = useMemo(() => {
    const v = identifier.trim();
    if (!v) return false;
    if (mode === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    return v.replace(/[^\d+]/g, "").length >= 7;
  }, [identifier, mode]);

  const lookup = async () => {
    if (!isValidIdentifier) return;
    setErr("");
    setWorking(true);
    try {
      const params = { site_id: tokenInfo.site_id };
      if (mode === "email") params.email = identifier.trim().toLowerCase();
      else params.phone = identifier.trim();
      const { data } = await kioskApi.get("/kiosk/visitor-lookup", { params });
      if (!data.found) {
        // Send them through the long flow with the identifier we already
        // have so they don't have to re-type it.
        nav(`/kiosk/sign-in?${mode}=${encodeURIComponent(identifier.trim())}`,
            { replace: true });
        return;
      }
      setMatch(data.visitor);
      // Pull the full active consent set up front — we need every active
      // doc id in the submission regardless of whether the visitor needs
      // to re-tick it. The kiosk auth scope on `/consent-documents/active`
      // is org-wide so this is safe.
      let activeDocs = [];
      try {
        const all = await kioskApi.get("/consent-documents/active");
        activeDocs = all.data || [];
        setAllActiveConsents(activeDocs);
      } catch { /* if this fails, submission may 422 — surface that gracefully */ }

      if (data.consent_status === "expired" && data.expired_consent_doc_ids?.length) {
        // Show ONLY the expired docs as checkboxes — the visitor doesn't
        // need to re-read policies they've already accepted at the
        // current version.
        const pending = activeDocs.filter(
          (c) => data.expired_consent_doc_ids.includes(c.id),
        );
        setPendingConsents(pending);
        setPhase(PHASES.consents);
      } else {
        setPhase(PHASES.confirm);
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.error === "returning_visitor_disabled") {
        setErr("Returning-visitor fast lane is disabled for this organisation.");
      } else if (det?.error === "site_mismatch") {
        setErr("Kiosk is paired to a different site — contact reception.");
      } else {
        setErr(det?.message || det?.error || e?.message || "Lookup failed");
      }
    } finally { setWorking(false); }
  };

  const consentsAllAccepted = pendingConsents.every((c) => accepted[c.id]);

  const submitSignIn = async () => {
    setErr("");
    setWorking(true);
    try {
      // Submit EVERY active consent doc. The expired ones were explicitly
      // re-accepted on the previous step; the still-valid ones are reaffirmed
      // by the "Sign me in" tap (the prior visit's acceptance remains on
      // record either way).
      const consentsArr = allActiveConsents.map((c) => ({ doc_id: c.id }));
      const { data } = await kioskApi.post("/visits/sign-in", {
        visitor_type: match.visitor_type || "Visitor",
        visitor_name: match.name,
        visitor_company: match.company || null,
        visitor_email: match.email || null,
        visitor_phone: match.phone || null,
        host_id: match.host_id_suggested || null,
        purpose: match.last_purpose || "Returning visit",
        vehicle_reg: match.vehicle_reg || null,
        photo_data_url: null,
        photo_bypass: true,
        signature_data_url: signature,
        consents: consentsArr.length ? consentsArr : null,
        // Phase C1.6 — tag the visit so the Live Dashboard ROI widget
        // can count it as a fast-lane sign-in.
        flow_source: "welcome_back",
      });
      setResult(data);
      setPhase(PHASES.done);
      setTimeout(() => nav("/kiosk", { replace: true }), 7000);
    } catch (e) {
      // The /visits/sign-in endpoint returns structured detail objects
      // (e.g. `{error: "missing_consents", missing_consents: [...]}`) on
      // validation failure — we can't render those directly as React
      // children, so coerce to a friendly string.
      const det = e?.response?.data?.detail;
      let msg;
      if (typeof det === "string") {
        msg = det;
      } else if (det && typeof det === "object") {
        if (det.error === "missing_consents") {
          msg = "Some required consents are still missing — please re-tick all the boxes above.";
        } else {
          msg = det.message || det.error || JSON.stringify(det);
        }
      } else {
        msg = e?.message || "Sign-in failed";
      }
      setErr(msg);
    } finally { setWorking(false); }
  };

  return (
    <KioskLayout branding={branding}>
      <div className="flex items-center justify-between mb-6 text-white/70 text-sm"
           data-testid="kiosk-welcome-back-page">
        <button
          onClick={() => (phase === PHASES.lookup ? nav("/kiosk", { replace: true }) : setPhase(PHASES.lookup))}
          className="inline-flex items-center gap-2 hover:text-white"
          data-testid="welcome-back-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>
        <span className="text-xs uppercase tracking-[0.3em] text-orange-300">
          <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1" />
          Welcome back
        </span>
        <button onClick={() => nav("/kiosk", { replace: true })}
                className="text-xs text-white/50 hover:text-white"
                data-testid="welcome-back-cancel-btn">
          {t("cancel")}
        </button>
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-2xl w-full mx-auto">
        {phase === PHASES.lookup && (
          <div data-testid="welcome-back-lookup">
            <h2 className="text-3xl md:text-4xl font-semibold text-center mb-3">
              Have we met before?
            </h2>
            <p className="text-white/70 text-center mb-8">
              Tap your email or phone to skip the long form.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <button
                onClick={() => { setMode("email"); setIdentifier(""); }}
                className={`rounded-xl px-6 py-4 border-2 transition-colors ${
                  mode === "email"
                    ? "bg-white text-navy-900 border-white"
                    : "bg-white/5 text-white border-white/20 hover:bg-white/10"
                }`}
                data-testid="welcome-back-mode-email"
              >
                <Mail className={`w-5 h-5 mx-auto mb-1 ${mode === "email" ? "text-orange-500" : ""}`} />
                <div className="text-sm font-semibold">Email</div>
              </button>
              <button
                onClick={() => { setMode("phone"); setIdentifier(""); }}
                className={`rounded-xl px-6 py-4 border-2 transition-colors ${
                  mode === "phone"
                    ? "bg-white text-navy-900 border-white"
                    : "bg-white/5 text-white border-white/20 hover:bg-white/10"
                }`}
                data-testid="welcome-back-mode-phone"
              >
                <Phone className={`w-5 h-5 mx-auto mb-1 ${mode === "phone" ? "text-orange-500" : ""}`} />
                <div className="text-sm font-semibold">Phone</div>
              </button>
            </div>

            <input
              autoFocus
              value={identifier}
              type={mode === "email" ? "email" : "tel"}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={mode === "email" ? "you@company.com" : "+44 7700 900123"}
              className="w-full bg-white/10 border border-white/20 rounded-2xl px-6 py-5 text-2xl text-white placeholder-white/40 focus:outline-none focus:border-orange-400"
              data-testid="welcome-back-identifier-input"
              onKeyDown={(e) => e.key === "Enter" && isValidIdentifier && lookup()}
            />

            {err && (
              <div className="mt-4 rounded-lg bg-red-500/15 border border-red-400/40 px-4 py-3 text-sm text-red-100 inline-flex items-center gap-2"
                   data-testid="welcome-back-error">
                <AlertCircle className="w-4 h-4" /> {err}
              </div>
            )}

            <button
              disabled={!isValidIdentifier || working}
              onClick={lookup}
              className="mt-6 w-full rounded-2xl bg-orange-500 hover:bg-orange-600 text-white px-8 py-5 text-xl font-semibold disabled:opacity-50"
              data-testid="welcome-back-lookup-btn"
            >
              {working ? "Looking up…" : "Find me"}
            </button>

            <button
              onClick={() => nav("/kiosk/sign-in", { replace: true })}
              className="mt-4 w-full text-white/60 hover:text-white text-sm underline"
              data-testid="welcome-back-skip-btn"
            >
              First-time visitor? Sign in the normal way →
            </button>
          </div>
        )}

        {phase === PHASES.consents && (
          <div data-testid="welcome-back-consents">
            <h2 className="text-2xl font-semibold mb-2">
              Welcome back, {match?.name?.split(" ")[0]}.
            </h2>
            <p className="text-white/70 mb-4 text-sm inline-flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-orange-300" />
              Our policies have changed since your last visit — please re-accept to continue.
            </p>
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-2">
              {pendingConsents.map((c) => (
                <div key={c.id} className="bg-white/5 border border-white/15 rounded-xl p-5"
                     data-testid={`welcome-consent-${c.id}`}>
                  <div className="font-semibold mb-2 flex items-center justify-between">
                    {c.title}
                    <span className="text-xs uppercase tracking-wider text-white/50">v{c.version}</span>
                  </div>
                  <div className="text-sm text-white/70 prose prose-invert max-h-[12rem] overflow-y-auto"
                       dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.body_html) }} />
                  <label className="mt-3 inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!accepted[c.id]}
                      onChange={(e) => setAccepted({ ...accepted, [c.id]: e.target.checked })}
                      className="w-4 h-4"
                      data-testid={`welcome-consent-cb-${c.id}`}
                    />
                    {t("i_accept")}
                  </label>
                </div>
              ))}
            </div>

            <button
              disabled={!consentsAllAccepted || working}
              onClick={() => setPhase(PHASES.confirm)}
              className="mt-6 w-full rounded-2xl bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold disabled:opacity-50"
              data-testid="welcome-back-consents-next"
            >
              Continue →
            </button>
          </div>
        )}

        {phase === PHASES.confirm && (
          <div className="text-center" data-testid="welcome-back-confirm">
            <h2 className="text-3xl md:text-4xl font-semibold mb-2">
              Welcome back, {match?.name?.split(" ")[0]}!
            </h2>
            <p className="text-white/70 mb-8 text-sm">
              {requiresSignature
                ? "Sign below to complete your sign-in."
                : "We've got you. Tap below to sign in."}
            </p>
            <div className="inline-block text-left bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 mb-6 min-w-[260px]">
              <Row label="Name" value={match?.name} />
              {match?.company && <Row label="Company" value={match.company} />}
              <Row label="Visitor type" value={match?.visitor_type || "Visitor"} />
              {match?.host_name_suggested && <Row label="Host" value={match.host_name_suggested} />}
              {match?.last_purpose && <Row label="Purpose" value={match.last_purpose} />}
            </div>

            {/* Phase C1.5 fix — mount the same signature canvas the standard
                sign-in flow uses when the visitor's type requires it. The
                outer testid is what the tester checklist asserts on; the
                inner `signature-canvas` testid (from `<SignaturePad />`) is
                used by the existing kiosk e2e suite. */}
            {requiresSignature && (
              <div className="text-left max-w-xl mx-auto mb-6"
                   data-testid="welcome-back-signature-canvas">
                <div className="text-xs text-white/60 mb-2 uppercase tracking-wider">
                  Signature <span className="text-red-300">*</span>
                </div>
                <SignaturePad onChange={setSignature} />
              </div>
            )}

            {err && <div className="text-red-300 mb-4 text-sm" data-testid="welcome-back-confirm-error">{err}</div>}
            <button
              onClick={submitSignIn}
              disabled={working || (requiresSignature && !signature)}
              className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white px-12 py-5 text-xl font-semibold disabled:opacity-50"
              data-testid="welcome-back-sign-in-btn"
            >
              {working ? "Signing in…" : "Sign me in"}
            </button>
            <p className="mt-4 text-xs text-white/50">
              {requiresPhoto
                ? "Photo & long form are skipped because you're a returning visitor."
                : "The long form is skipped because you're a returning visitor."}
            </p>
          </div>
        )}

        {phase === PHASES.done && result && (
          <div className="text-center" data-testid="welcome-back-done">
            <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-400/40 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10 text-green-300" />
            </div>
            <h2 className="text-4xl font-bold mb-2">You're signed in</h2>
            <p className="text-white/70 mb-6">Your host has been notified.</p>
            {result.sign_out_pin && (
              <div className="inline-block bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 text-left">
                <div className="text-xs uppercase tracking-wider text-orange-300">Sign-out PIN</div>
                <div className="text-5xl font-bold tracking-[0.4em] mt-2 mb-3"
                     data-testid="welcome-back-signout-pin">{result.sign_out_pin}</div>
                <div className="text-xs text-white/60 flex items-center gap-1.5">
                  <Hash className="w-3 h-3" /> Use this PIN at the kiosk to sign out
                </div>
              </div>
            )}
            <p className="mt-8 text-xs text-white/40">Returning to home in a few seconds…</p>
          </div>
        )}
      </div>
    </KioskLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-6 py-1.5 text-sm">
      <span className="text-white/50">{label}</span>
      <span className="text-white font-medium">{value || "—"}</span>
    </div>
  );
}
